import React, { useCallback } from 'react';
import PropTypes from 'prop-types';

import { DateService, IntlManager, SiteConfigManager } from '@atlas/core';
import { Label } from '@atlas/ui';

const BonusesTableRow = ({ active = false, bonus, canceled = false, expired = false, wallet }) => {
  let financeSettings = {};

  const {
    bonusAmount,
    bonusAssignmentTime,
    copiedBonusSettings,
    createTime,
    endTime,
    expirationTime,
    financeDataList,
    requiredWagering,
    timeToWager,
    wager,
    wagerStartTime,
  } = bonus;

  const dateFormat = SiteConfigManager.getConfig('config.config.date.dateFormat', 'DD.MM.YYYY');

  const formattedAssignmentTime = bonusAssignmentTime
    ? DateService.getFormattedDate(bonusAssignmentTime, dateFormat)
    : null;
  const formattedCreateTime = DateService.getFormattedDate(createTime, dateFormat);
  const formattedEndTime = DateService.getFormattedDate(endTime, dateFormat);
  const formattedExpirationTime = expirationTime
    ? DateService.getFormattedDate(expirationTime, dateFormat)
    : null;
  const formattedActivatedTime = createTime
    ? DateService.getFormattedDate(createTime, dateFormat)
    : null;

  const calculatedExpiresAt =
    wagerStartTime && (timeToWager || copiedBonusSettings?.timeToWager)
      ? DateService.getFormattedDate(
          new Date(
            new Date(wagerStartTime).getTime() + (timeToWager || copiedBonusSettings.timeToWager),
          ),
          dateFormat,
        )
      : null;

  const formattedExpiresAt = endTime
    ? DateService.getFormattedDate(endTime, dateFormat)
    : calculatedExpiresAt;

  for (let i = 0; i < financeDataList.length; i++) {
    if (financeDataList[i].currency === wallet.currency) {
      financeSettings = financeDataList[i];
      break;
    }
  }

  const renderBonusRow = useCallback(() => {
    if (expired) {
      return (
        <>
          <div>
            <span className="sub-title-text">
              <Label message="Maximum bonus amount" />
              :&nbsp;
            </span>
            <span className="title-small-text">{IntlManager.formatMoney(bonusAmount)}</span>
          </div>
          <div>
            <span className="sub-title-text">
              <Label message="amount_of_bets_for_full_wagering" />
              :&nbsp;
            </span>
            <span className="title-small-text">{IntlManager.formatMoney(requiredWagering)}</span>
          </div>
          <div>
            <span className="sub-title-text">
              <Label message="Wager" />
              :&nbsp;
            </span>
            <span className="title-small-text">{wager}</span>
          </div>

          <div>
            <span className="sub-title-text">
              <Label message="expired" />
              :&nbsp;
            </span>
            <span className="title-small-text">{formattedExpiresAt}</span>
          </div>
        </>
      );
    }

    if (active || canceled) {
      return (
        <>
          <div>
            <span className="sub-title-text">
              <Label message="Maximum bonus amount" />
              :&nbsp;
            </span>
            <span className="title-small-text">{IntlManager.formatMoney(bonusAmount)}</span>
          </div>
          <div>
            <span className="sub-title-text">
              <Label message="amount_of_bets_for_full_wagering" />
              :&nbsp;
            </span>
            <span className="title-small-text">{IntlManager.formatMoney(requiredWagering)}</span>
          </div>
          <div>
            <span className="sub-title-text">
              <Label message="Wager" />
              :&nbsp;
            </span>
            <span className="title-small-text">{wager}</span>
          </div>
          <div>
            <span className="sub-title-text">
              <Label message="activated" />
              :&nbsp;
            </span>
            <span className="title-small-text with-padding">
              {formattedActivatedTime || formattedCreateTime}
            </span>
          </div>
        </>
      );
    }

    return (
      <>
        <div>
          <span className="sub-title-text">
            <Label message="Maximum bonus amount" />
            :&nbsp;
          </span>
          <span className="title-small-text">
            {IntlManager.formatMoney(financeSettings.maxBonusAmount)}
          </span>
        </div>
        <div>
          <span className="sub-title-text">
            <Label message="Minimum deposit amount" />
            :&nbsp;
          </span>
          <span className="title-small-text">
            {IntlManager.formatMoney(financeSettings.minDeposit)}
          </span>
        </div>
        <div>
          <span className="sub-title-text">
            <Label message="Period for wagering" />
            :&nbsp;
          </span>
          <span className="title-small-text">
            <span>{Math.floor(timeToWager / (1000 * 60 * 60 * 24))}</span>
            <Label message="days" />
          </span>
        </div>
        <div>
          <span className="sub-title-text">
            <Label message="Wager" />
            :&nbsp;
          </span>
          <span className="title-small-text">{wager}</span>
        </div>
        <div>
          <span className="sub-title-text">
            <Label message="assigned" />
            :&nbsp;
          </span>
          <span className="title-small-text with-padding">{formattedAssignmentTime}</span>
          <span className="sub-title-text">
            <Label message="expires" />
            :&nbsp;
          </span>
          <span className="title-small-text">{formattedExpirationTime || formattedEndTime}</span>
        </div>
      </>
    );
  }, [active, canceled, bonus, expired]);

  return renderBonusRow();
};

BonusesTableRow.propTypes = {
  active: PropTypes.bool,
  bonus: PropTypes.object.isRequired,
  canceled: PropTypes.bool,
  now: PropTypes.number.isRequired,
  wallet: PropTypes.object.isRequired,
};

export default BonusesTableRow;
